import React, { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { ApolloError } from '@apollo/client';
import Paper from '@mui/material/Paper';
import { makeStyles } from '@mui/styles';
import { Theme } from '@mui/material/styles';
import Divider from '@mui/material/Divider';
import Helmet from '../../../components/Helmet';
import CircularProgress from '../../../components/_loading/CircularProgress';
import Context from '../../../context';
import { useCompanyQuery } from '../../../../graphql/query/company/useCompanyQuery';
import NotFound from '../../../components/_error/NotFound';
import BusinessLayout from '../../../layout/Business';
import { useUpdateCompanyCommercialPracticeMutation } from '../../../../graphql/mutation/company/updateCompanyCommercialPracticeMutation';
import useForm from '../../../components/_hook/useForm';
import useNotification from '../../../components/_hook/useNotification';
import useLanguage from '../../../components/_hook/useLanguage';
import StickyFormFooter from '../../../components/_form/StickyFormFooter';
import Select, { IValues } from '../../../components/_select/_module/Select';
import SelectCurrency from '../../../components/_select/SelectCurrency';
import SelectLanguage from '../../../components/_select/SelectLanguage';

const useStyles = makeStyles<Theme>((theme: Theme) => ({
  paper: {
    margin: theme.spacing(1),
    padding: theme.spacing(3),
    width: '100%',
  },
  contentCard: {
    width: '100%',
    backgroundColor: theme.palette.mode === 'light' ? '#f3f3f3' : 'inherit',
  },
}));

function BusinessCommercialPractice() {
  interface IFormValue {
    currenciesAccepted: string[];
    paymentMethods: string[];
    termsOfDelivery: string[];
    spokenLanguages: string[];
  }

  const classes = useStyles();
  const { notification } = useNotification();
  const { t } = useTranslation();
  const { getText } = useLanguage();
  const { company, isSuperAdmin } = useContext(Context);
  const companyFragment = `
    _id
    name {
        la
        text
    }
    infos {
      commercial_practices {
        currencies_accepted
        payment_methods
        terms_of_delivery
        spoken_language
      }
    }
  `;
  const { data, loading } = useCompanyQuery(companyFragment, {
    variables: {
      filter: {
        _id: company._id as string,
      },
      limit: 1,
    },
  });

  function onCompleted() {
    notification.success(t('done'));
  }

  function onError(e: ApolloError) {
    notification.error(e.message);
  }

  const [
    updateCompanyCommercialPracticeMutation,
    { loading: isUpdatingCompanyCommercialPractice },
  ] = useUpdateCompanyCommercialPracticeMutation(
    `updatedCompany {
				${companyFragment}
			}`,
    {
      onCompleted,
      onError,
    }
  );

  async function onSubmitForm(tempValues: IFormValue) {
    if (data) {
      await updateCompanyCommercialPracticeMutation({
        variables: {
          _id: data.companies.items[0]._id,
          currenciesAccepted: tempValues.currenciesAccepted,
          paymentMethods: tempValues.paymentMethods,
          termsOfDelivery: tempValues.termsOfDelivery,
          spokenLanguages: tempValues.spokenLanguages,
        },
      });
    }
  }

  const {
    values,
    setValue,
    handleSubmit,
    errors,
    setValues,
  } = useForm<IFormValue>({
    defaultValues: {
      currenciesAccepted: [],
      paymentMethods: [],
      termsOfDelivery: [],
      spokenLanguages: [],
    },
    onSubmit: onSubmitForm,
  });

  useEffect(() => {
    if (!loading && data && data.companies.items.length) {
      const tempCompany = data.companies.items[0];
      const companyInfos = tempCompany.infos || {};
      const companyInfoCommercialPractices =
        companyInfos.commercial_practices || {};

      setValues({
        currenciesAccepted:
          companyInfoCommercialPractices.currencies_accepted || [],
        paymentMethods: companyInfoCommercialPractices.payment_methods || [],
        termsOfDelivery: companyInfoCommercialPractices.terms_of_delivery || [],
        spokenLanguages: companyInfoCommercialPractices.spoken_language || [],
      });
    }
  }, [loading]);

  const queryCompany = data ? data.companies.items[0] : null;

  if (loading) return <CircularProgress center color="primary" />;
  if (!queryCompany) return <NotFound />;

  return (
    <Paper square className={classes.paper}>
      <Helmet title={`${t('commercial_practice')} | ${t('business')}`} />
      <Grid container spacing={2}>
        <Grid container justifyContent="space-between" spacing={1}>
          <Grid item>
            <Typography variant="h6">
              {t('update')} {getText(company.name)} {t('commercial_practice')}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Divider />
          </Grid>
        </Grid>
        <Grid container item xs={12} spacing={3}>
          <Grid item xs={12}>
            <SelectCurrency<IValues>
              isClearable
              fullWidth
              label={t('currencies_accepted')}
              value={values.currenciesAccepted}
              queryVariables={{
                filter: {},
              }}
              isMulti
              onChange={(options) =>
                setValue(
                  'currenciesAccepted',
                  options.map((option) => option.value)
                )
              }
              error={Boolean(errors.currenciesAccepted)}
              helperText={errors.currenciesAccepted}
            />
          </Grid>
          <Grid item xs={12}>
            <Select<IValues>
              isClearable
              options={[
                {
                  label: t('payments_boe'),
                  value: 'boe',
                },
                {
                  label: t('payments_bt'),
                  value: 'bt',
                },
                {
                  label: t('payments_cc'),
                  value: 'cc',
                },
                {
                  label: t('payments_ch'),
                  value: 'ch',
                },
                {
                  label: t('payments_cod'),
                  value: 'cod',
                },
                {
                  label: t('payments_cop'),
                  value: 'cop',
                },
                {
                  label: t('payments_cs'),
                  value: 'cs',
                },
                {
                  label: t('payments_da'),
                  value: 'da',
                },
                {
                  label: t('payments_dc'),
                  value: 'dc',
                },
                {
                  label: t('payments_dr'),
                  value: 'dr',
                },
                {
                  label: t('payments_dp'),
                  value: 'dp',
                },
                {
                  label: t('payments_imo'),
                  value: 'imo',
                },
                {
                  label: t('payments_lc'),
                  value: 'lc',
                },
                {
                  label: t('payments_mt'),
                  value: 'mt',
                },
                {
                  label: t('payments_op'),
                  value: 'op',
                },
                {
                  label: t('payments_pn'),
                  value: 'pn',
                },
                {
                  label: t('payments_sw'),
                  value: 'sw',
                },
                {
                  label: t('payments_tt'),
                  value: 'tt',
                },
              ]}
              isMulti
              label={t('payment_method', { count: 2 })}
              fullWidth
              menuPosition="fixed"
              value={values.paymentMethods}
              onChange={(options) =>
                setValue(
                  'paymentMethods',
                  options.map((option) => option.value)
                )
              }
              error={Boolean(errors.paymentMethods)}
              helperText={errors.paymentMethods}
            />
          </Grid>
          <Grid item xs={12}>
            <Select<IValues>
              isClearable
              options={[
                {
                  label: t('incoterms_any'),
                  options: [
                    {
                      label: t('incoterms_exw'),
                      value: 'exw',
                    },
                    {
                      label: t('incoterms_fca'),
                      value: 'fca',
                    },
                    {
                      label: t('incoterms_cpt'),
                      value: 'cpt',
                    },
                    {
                      label: t('incoterms_cip'),
                      value: 'cip',
                    },
                    {
                      label: t('incoterms_dat'),
                      value: 'dat',
                    },
                    {
                      label: t('incoterms_dap'),
                      value: 'dap',
                    },
                    {
                      label: t('incoterms_ddp'),
                      value: 'ddp',
                    },
                  ],
                },
                {
                  label: t('incoterms_sea'),
                  options: [
                    {
                      label: t('incoterms_fas'),
                      value: 'fas',
                    },
                    {
                      label: t('incoterms_fob'),
                      value: 'fob',
                    },
                    {
                      label: t('incoterms_cfr'),
                      value: 'cfr',
                    },
                    {
                      label: t('incoterms_cif'),
                      value: 'cif',
                    },
                  ],
                },
              ]}
              isMulti
              label={t('terms_of_delivery')}
              fullWidth
              menuPosition="fixed"
              value={values.termsOfDelivery}
              onChange={(options) =>
                setValue(
                  'termsOfDelivery',
                  options.map((option) => option.value)
                )
              }
              error={Boolean(errors.termsOfDelivery)}
              helperText={errors.termsOfDelivery}
            />
          </Grid>
          <Grid item xs={12}>
            <Grid item xs={12}>
              <SelectLanguage<IValues>
                isClearable
                isMulti
                fullWidth
                label={t('spoken_language')}
                value={values.spokenLanguages}
                queryVariables={{
                  filter: {},
                }}
                onChange={(options) => {
                  setValue(
                    'spokenLanguages',
                    options.map((option) => option.value)
                  );
                }}
                error={Boolean(errors.spokenLanguages)}
                helperText={errors.spokenLanguages}
              />
            </Grid>
          </Grid>
        </Grid>
        <StickyFormFooter
          onSubmit={handleSubmit}
          isSubmitting={isUpdatingCompanyCommercialPractice}
        />
      </Grid>
    </Paper>
  );
}

export default () => <BusinessLayout main={<BusinessCommercialPractice />} />;
